import {
  DEFAULT_BODY,
  ARMED_LOCOMOTION_CLIP_IDS,
  LOCOMOTION_CLIP_IDS,
  loadEssentialBodyFrames,
  loadCreatureBodyLocomotionFrames,
  loadEquipItemLocomotionFrames,
  loadBodyActionFrames,
  loadEquipItemActionFrames,
  type BodyFrames,
  type EquipFrames,
  type PlayerFrames,
} from './playerArt';
import {
  mobileAtlasUrlFor,
  equipAtlasUrlFor,
  unloadAtlas,
} from './playerArt';
import {
  equipment as equipDefs,
  isGhostBody,
  mountBody,
  RIDE_CLIP_IDS,
  SHROUD_ID,
} from '../sim/content';
import type { PlayerLook } from './buildPlayerLayers';
import type {
  SpriteResidency} from './spriteResidency';
import {
  AtlasRefs,
  estimateClipBytes,
  estimateFramesBytes,
  type ResidencyStats,
} from './spriteResidency';

/**
 * A body/equipment atlas becoming available: `kind` says which map grew, `id` is the
 * body id (number) or equipment content id (string) that just resolved. The renderer
 * subscribes to swap the grounded fallback for the real frames on the sprites that use
 * it (see {@link GameRenderer}).
 */
export type FramesLoadListener = (
  kind: 'body' | 'equip',
  id: number | string,
  clip?: string,
) => void;

export interface AssetLoadRunner {
  run<T>(load: () => Promise<T>): Promise<T>;
}

/**
 * Owns lazy, on-first-sighting streaming of the body + equipment sprite atlases
 * (ADR-0247 / issue #474). A fresh visitor used to eager-load **all** ~95 MB of body +
 * equipment PNG atlases at boot - the whole arena creature bestiary and every one of
 * the ~970 equipment layers - before the world rendered, even for a naked character in
 * an empty bank. This store instead:
 *
 *  - preloads only human locomotion plus the death shroud up front,
 *  - streams locomotion for a newly visible creature/equipment id through
 *    {@link requestLook}, and
 *  - fetches every combat, death, cast, and emote clip separately through
 *    {@link loadClip} when gameplay first needs it.
 *
 * The two records it fills ({@link bodyFrames}/{@link equipFrames}) are the **same
 * objects** the renderer's `buildPlayerLayers` reads synchronously, mutated in place as
 * atlases arrive - so no render code needs to become async. Until a body/equipment
 * atlas resolves, `buildPlayerLayers`' existing fallbacks apply (an unloaded body draws
 * as the default human body's grounded stand pose; an unloaded equipment layer is
 * simply skipped - the character renders undressed), so a streaming sprite never floats
 * or crashes; it just pops in dressed/into its real body a beat later.
 *
 * All loads are **deduped** per id and clip, and **absence is terminal** (an id or clip
 * with no baked art is remembered so it is never retried). A swap that lands after the entity despawned
 * is harmless: the store only mutates its own records and fires the listener; the
 * renderer reconciles against the live world, so a stale id is simply never used.
 */
export class SpriteAtlasStore {
  /** Body id -> its loaded {@link PlayerFrames}. Read directly by `buildPlayerLayers`. */
  readonly bodyFrames: BodyFrames = {};
  /** Equipment content id -> its loaded {@link PlayerFrames}. Read by `buildPlayerLayers`. */
  readonly equipFrames: EquipFrames = {};

  // In-flight de-dupe: an id currently loading. Absent-set: an id proven to have no
  // baked art (never retried). An id is "resolved" once it is in its frames record or
  // its absent-set - that is the signal {@link isLookLoaded} reports off.
  private readonly bodyAbsent = new Set<number>();
  private readonly equipAbsent = new Set<string>();
  private readonly bodyPromises = new Map<number, Promise<void>>();
  private readonly equipPromises = new Map<string, Promise<void>>();
  private readonly clipPromises = new Map<string, Promise<void>>();
  /**
   * Clip keys currently merged into their frames record. Cleared per-clip when a
   * residency eviction drops the clip (so it can stream again on next use) - unlike
   * {@link clipAbsent}, which is permanent.
   */
  private readonly loadedClips = new Set<string>();
  /**
   * Clip keys proven to have no baked art (or a failed decode): terminal absence,
   * never retried - deliberately NOT cleared by eviction, so an absent clip can
   * never enter a load loop across evict/reload cycles (issue #625).
   */
  private readonly clipAbsent = new Set<string>();

  private readonly listeners = new Set<FramesLoadListener>();

  /** Reference counts per shared base-atlas URL; unloads Pixi's cache entry at zero. */
  private readonly atlasRefs: AtlasRefs;

  /**
   * @param residency optional bounded-residency ledger (issue #625): when wired, every
   * streamed body/equipment locomotion bundle and every streamed clip is registered
   * with its estimated decoded byte weight, pinnable via {@link pinLook}, and evicted
   * LRU-first when the soft budget is exceeded. Absent (tests/tools), the store keeps
   * everything resident exactly as before.
   */
  constructor(
    private readonly assetLoads?: AssetLoadRunner,
    private readonly residency?: SpriteResidency,
    atlasRefs?: AtlasRefs,
  ) {
    this.atlasRefs = atlasRefs ?? new AtlasRefs((url) => unloadAtlas(url));
  }

  private run<T>(load: () => Promise<T>): Promise<T> {
    return this.assetLoads ? this.assetLoads.run(load) : load();
  }

  /** The observable residency seam (bytes, budget, mode); `undefined` when unwired. */
  residencyStats(): ResidencyStats | undefined {
    return this.residency?.stats();
  }

  /** The residency keys a look's presentation depends on (body + worn layers/shroud). */
  private lookResidencyKeys(look: PlayerLook): string[] {
    const body = look.body ?? DEFAULT_BODY;
    const keys = [`body:${body}`];
    // A ghost renders the death shroud in place of its equipment (buildPlayerLayers),
    // and its body frames alias the living body's - pin the shroud, not the outfit.
    if (isGhostBody(body)) {
      keys.push(`equip:${SHROUD_ID}`);
      return keys;
    }
    for (const item of look.equipment ?? []) keys.push(`equip:${item.id}`);
    // A mount is a second BODY atlas drawn under this look (ADR-0442), so it is pinned
    // exactly like the rider's own body - otherwise a mounted rider standing still
    // could have the camel evicted out from under them while they remain visible.
    if (look.mount !== undefined) keys.push(`body:${look.mount}`);
    return keys;
  }

  /**
   * Pin every atlas a look renders from for the lifetime of one consumer (a visible
   * sprite, a raised corpse, a preview) - issue #625. Pins are live references in the
   * {@link SpriteResidency}: a pinned look is never evicted, even over budget, and a
   * pin taken while the look is still streaming protects the atlases the moment they
   * land (pending pins). Returns an idempotent release; a store with no residency
   * wired returns a no-op. Streamed one-shot clips ride their parent id's pin.
   */
  pinLook(look: PlayerLook): () => void {
    const residency = this.residency;
    if (!residency) return () => undefined;
    const keys = this.lookResidencyKeys(look);
    for (const key of keys) residency.retain(key);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      for (const key of keys) residency.release(key);
    };
  }

  /**
   * Register one streamed locomotion bundle in the residency ledger: its decoded byte
   * weight, the base-atlas URLs it decoded (shared-texture refcounted), and a destroy
   * that unwinds exactly what loading built - the frames record entry, the per-clip
   * loaded marks (so an evicted clip streams again), and the atlas references (the
   * base textures are unloaded only when *their* count hits zero). No-op when no
   * residency is wired.
   */
  private registerResidency(
    kind: 'body' | 'equip',
    id: number | string,
    frames: Partial<PlayerFrames>,
    urls: (string | undefined)[],
  ): void {
    if (!this.residency) return;
    const key = `${kind}:${id}`;
    const held = urls.filter((url): url is string => url !== undefined);
    for (const url of held) this.atlasRefs.acquire(url);
    this.residency.insert(key, estimateFramesBytes(frames), () => {
      if (kind === 'body') delete this.bodyFrames[id as number];
      else delete this.equipFrames[id as string];
      this.forgetClips(kind, id);
      for (const url of held) this.atlasRefs.release(url);
    });
  }

  /** The per-clip twin of {@link registerResidency}, parented to its bundle. */
  private registerClipResidency(
    kind: 'body' | 'equip',
    id: number | string,
    clip: string,
    frames: PlayerFrames['stand'],
  ): void {
    if (!this.residency) return;
    const key = `${kind}:${id}:${clip}`;
    const url =
      kind === 'body' ? mobileAtlasUrlFor(id as number, clip) : equipAtlasUrlFor(id as string, clip);
    if (url) this.atlasRefs.acquire(url);
    this.residency.insert(
      key,
      estimateClipBytes(frames),
      () => {
        this.loadedClips.delete(key);
        const target = kind === 'body' ? this.bodyFrames[id as number] : this.equipFrames[id as string];
        if (target) delete (target as unknown as Record<string, unknown>)[clip];
        if (url) this.atlasRefs.release(url);
      },
      `${kind}:${id}`,
    );
  }

  /** Forget the loaded marks of every streamed clip under an evicted bundle. */
  private forgetClips(kind: 'body' | 'equip', id: number | string): void {
    const prefix = `${kind}:${id}:`;
    for (const key of [...this.loadedClips]) {
      if (key.startsWith(prefix)) this.loadedClips.delete(key);
    }
  }

  /**
   * Load the login-critical body set (human 400/401 + ghost aliases) into
   * {@link bodyFrames}. Awaited before the renderer is built, exactly where the old
   * eager `loadBodyFrames()`/`loadEquipFrames()` were awaited - so a fresh visitor
   * still has everything needed to render themselves and the human crowd, but pays for
   * none of the creature bestiary or the ~970 equipment atlases until they are seen.
   */
  async preloadEssentials(): Promise<void> {
    // The death shroud is login-critical too: any duel death repaints a player as a
    // ghost (drawn as the shroud, not their equipment), and streaming it on first
    // sighting would flash the bare grey body for a beat. Load it alongside the bodies.
    const [essential] = await Promise.all([
      this.run(() => loadEssentialBodyFrames()),
      this.loadEquip(SHROUD_ID),
    ]);
    Object.assign(this.bodyFrames, essential);
    // The essential human bodies never enter the residency ledger (permanently
    // resident), but the shroud loads through the generic equip path - pin it forever
    // so a duel death can always dress its ghost without a re-stream (issue #625).
    this.residency?.retain(`equip:${SHROUD_ID}`);
  }

  /** Subscribe to atlas-ready events; call {@link removeLoadListener} to unsubscribe. */
  addLoadListener(listener: FramesLoadListener): void {
    this.listeners.add(listener);
  }

  removeLoadListener(listener: FramesLoadListener): void {
    this.listeners.delete(listener);
  }

  private emit(kind: 'body' | 'equip', id: number | string, clip?: string): void {
    for (const listener of this.listeners) listener(kind, id, clip);
  }

  /** Whether a body id needs no further loading (it is loaded, or proven absent). */
  private bodyResolved(body: number): boolean {
    return body in this.bodyFrames || this.bodyAbsent.has(body);
  }

  /** Whether an equipment id needs no further loading (it is loaded, or proven absent). */
  private equipResolved(id: string): boolean {
    return id in this.equipFrames || this.equipAbsent.has(id);
  }

  /**
   * Whether every atlas a look needs is already resolved - i.e. nothing about this
   * look is still streaming in. The renderer uses this to decide whether a freshly
   * built sprite is final or must be marked "pending" for a later swap.
   */
  isLookLoaded(look: PlayerLook): boolean {
    const body = look.body ?? DEFAULT_BODY;
    if (!this.bodyResolved(body)) return false;
    // A ghost body is drawn as the death shroud (buildPlayerLayers), not the look's
    // own equipment - so its readiness hinges on the shroud atlas, not on `equipment`.
    if (isGhostBody(body)) return this.equipResolved(SHROUD_ID);
    const replacementMount =
      look.mount !== undefined &&
      mountBody(look.mount)?.mountPresentation === 'replacement';
    if (replacementMount) return this.bodyResolved(look.mount!);
    for (const item of look.equipment ?? []) {
      if (!this.equipResolved(item.id)) return false;
    }
    // The mount's own body atlas must be resolved before a mounted look is final
    // (ADR-0442); until then the rider is drawn on foot and swaps onto the camel when
    // it arrives, the same pop-in every streamed body gets.
    if (look.mount !== undefined && !this.bodyResolved(look.mount)) return false;
    return true;
  }

  /**
   * Kick off (fire-and-forget) the load of every atlas a look needs that isn't already
   * loaded or loading - the body plus each worn equipment layer. Safe to call every
   * frame for the same look: resolved and in-flight ids short-circuit, so a look whose
   * atlases are all present does no work.
   */
  requestLook(look: PlayerLook): void {
    // Stamp the look's resident atlases as in-use so LRU eviction prefers looks
    // nobody has requested lately (cheap map bumps; called per frame per sprite).
    if (this.residency) {
      for (const key of this.lookResidencyKeys(look)) this.residency.touch(key);
    }
    const body = look.body ?? DEFAULT_BODY;
    this.ensureBody(body);
    // A ghost body renders the synthetic death shroud in place of the look's own
    // equipment (buildPlayerLayers), so stream the shroud atlas - it is never in
    // `equipment`, and without this a ghost draws as the bare grey body fallback.
    if (isGhostBody(body)) {
      this.ensureEquip(SHROUD_ID);
      return;
    }
    let armed = false;
    for (const item of look.equipment ?? []) {
      this.ensureEquip(item.id);
      armed ||= equipDefs.has(item.id) && equipDefs.get(item.id).layer === 'accessory';
    }
    // A held accessory switches the whole character stack to the armed gait.
    // Stream those two clips on first sight so the body and light retain their UO
    // hand bounce instead of permanently falling back to unarmed walk/run frames.
    if (armed) {
      for (const clip of ARMED_LOCOMOTION_CLIP_IDS) void this.loadClip(look, clip);
    }
    // Mounted (ADR-0442): stream the mount's own body atlas, plus the rider's three
    // seated clips. The ride clips sit OUTSIDE the eager locomotion bundle for the same
    // reason the armed gait does - almost nobody is mounted at any moment, so making
    // every visitor download them at login would be pure waste - and `loadClip` fetches
    // them for the body and each worn equipment layer alike, which is what keeps a
    // rider's hair, hat, and clothes seated with them rather than standing on the saddle.
    if (look.mount !== undefined) {
      this.ensureBody(look.mount);
      if (mountBody(look.mount)?.mountPresentation !== 'replacement') {
        for (const clip of RIDE_CLIP_IDS) void this.loadClip(look, clip);
      }
    }
  }

  /** Stream one creature body's atlas in the background if not already resolved/loading. */
  ensureBody(body: number): void {
    void this.loadBody(body);
  }

  private loadBody(body: number): Promise<void> {
    if (this.bodyResolved(body)) return Promise.resolve();
    const existing = this.bodyPromises.get(body);
    if (existing) return existing;
    const promise = this.run(() => loadCreatureBodyLocomotionFrames(body))
      .then((frames) => {
        if (frames) {
          this.bodyFrames[body] = frames;
          this.registerResidency(
            'body',
            body,
            frames,
            LOCOMOTION_CLIP_IDS.map((action) => mobileAtlasUrlFor(body, action)),
          );
          this.emit('body', body);
        } else {
          // No distinct creature art (unregistered / people / ghost / unbaked): mark
          // absent so it is never retried; the renderer's default-body fallback stands.
          this.bodyAbsent.add(body);
        }
      })
      .catch(() => {
        // A failed decode degrades to the default-body fallback, never a crash; mark
        // absent so a broken atlas isn't refetched every frame.
        this.bodyAbsent.add(body);
      })
      .finally(() => this.bodyPromises.delete(body));
    this.bodyPromises.set(body, promise);
    return promise;
  }

  /** Stream one equipment layer's atlas in the background if not already resolved/loading. */
  ensureEquip(id: string): void {
    void this.loadEquip(id);
  }

  /**
   * Await one equipment layer's atlas, resolving once it is loaded (or proven absent).
   * Deduped across concurrent callers so the wardrobe preview, the notification-icon
   * renderer, and the world crowd share a single in-flight load per id. Used by the
   * off-world preview surfaces that must draw a specific item and cannot rely on the
   * renderer's per-frame swap.
   */
  loadEquip(id: string): Promise<void> {
    if (this.equipResolved(id)) return Promise.resolve();
    const existing = this.equipPromises.get(id);
    if (existing) return existing;
    // An id with no registered equipment def has no atlas to bake - resolve it absent
    // immediately rather than assembling empty clips forever.
    if (!equipDefs.has(id)) {
      this.equipAbsent.add(id);
      return Promise.resolve();
    }
    const promise = this.run(() => loadEquipItemLocomotionFrames(id))
      .then((frames) => {
        this.equipFrames[id] = frames;
        this.registerResidency(
          'equip',
          id,
          frames,
          LOCOMOTION_CLIP_IDS.map((action) => equipAtlasUrlFor(id, action)),
        );
        this.emit('equip', id);
      })
      .catch(() => {
        // A failed decode degrades to an undressed layer, never a crash.
        this.equipAbsent.add(id);
      })
      .finally(() => {
        this.equipPromises.delete(id);
      });
    this.equipPromises.set(id, promise);
    return promise;
  }

  /**
   * Load one non-locomotion clip for a look and merge it into the live frame records.
   * Concurrent requests share one promise per body/equipment/clip tuple. Missing art is
   * remembered as resolved so an emote with no bake never causes repeated network work.
   */
  async loadClip(look: PlayerLook, clip: string): Promise<void> {
    if ((LOCOMOTION_CLIP_IDS as readonly string[]).includes(clip)) {
      this.requestLook(look);
      return;
    }
    const body = look.body ?? DEFAULT_BODY;
    const tasks: Promise<void>[] = [this.loadBodyClip(body, clip)];
    const equipment = isGhostBody(body) ? [{ id: SHROUD_ID }] : (look.equipment ?? []);
    for (const item of equipment) tasks.push(this.loadEquipClip(item.id, clip));
    await Promise.all(tasks);
  }

  private loadBodyClip(body: number, clip: string): Promise<void> {
    return this.loadOneClip('body', body, clip, async () => {
      await this.loadBody(body);
      const target = this.bodyFrames[body];
      if (!target) return undefined;
      return loadBodyActionFrames(body, clip, target.stand);
    });
  }

  private loadEquipClip(id: string, clip: string): Promise<void> {
    return this.loadOneClip('equip', id, clip, async () => {
      await this.loadEquip(id);
      if (!this.equipFrames[id]) return undefined;
      return loadEquipItemActionFrames(id, clip);
    });
  }

  private loadOneClip(
    kind: 'body' | 'equip',
    id: number | string,
    clip: string,
    load: () => Promise<PlayerFrames['stand'] | undefined>,
  ): Promise<void> {
    const key = `${kind}:${id}:${clip}`;
    if (this.loadedClips.has(key)) {
      this.residency?.touch(key);
      return Promise.resolve();
    }
    if (this.clipAbsent.has(key)) return Promise.resolve();
    const existing = this.clipPromises.get(key);
    if (existing) return existing;
    const promise = this.run(load)
      .then((frames) => {
        const target = kind === 'body' ? this.bodyFrames[id as number] : this.equipFrames[id];
        // The parent bundle was evicted while this clip streamed: don't latch anything
        // - a later sighting reloads the bundle and re-requests the clip fresh.
        if (!target) return;
        if (frames) {
          (target as unknown as Record<string, PlayerFrames['stand']>)[clip] = frames;
          this.loadedClips.add(key);
          this.registerClipResidency(kind, id, clip, frames);
          this.emit(kind, id, clip);
        } else {
          // No baked art for this clip: terminal absence, never retried - not even
          // after its bundle is evicted and reloaded (issue #625's no-retry-loop rule).
          this.clipAbsent.add(key);
        }
      })
      .catch(() => {
        // A failed decode degrades to the stand pose forever, never a refetch loop.
        this.clipAbsent.add(key);
      })
      .finally(() => {
        this.clipPromises.delete(key);
      });
    this.clipPromises.set(key, promise);
    return promise;
  }
}
